import {
    type FC,
    type ReactNode,
    type ChangeEvent,
    type FocusEvent,
    type KeyboardEvent,
} from 'react';
import { Input } from '../Input/index.tsx';
import classnames from 'classnames';
import { SIZES, DIRECTIONS } from '../Common/constants.ts';
import styles from './main.scss';

export type NumberInputEventHandler = (value: number | null) => void;

export type PropsType = {
    dataTn: string;
    value: string | number | null;
    onChange: NumberInputEventHandler;
    alignRight?: boolean;
    alignCenter?: boolean;
    autoFocus?: boolean;
    disabled?: boolean;
    errorDataTn?: string;
    errorMessage?: ReactNode | { __html: string };
    errorMessageAlignment?: typeof DIRECTIONS.right | typeof DIRECTIONS.left;
    hasAnimatedPlaceholder?: boolean;
    hasError?: boolean;
    horizontalSpacing?: typeof SIZES.def | typeof SIZES.small;
    label?: ReactNode;
    leftDecorator?: ReactNode;
    locale?: string;
    name?: string;
    onBlur?: NumberInputEventHandler;
    onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
    placeholder?: string;
    rightDecorator?: ReactNode;
    rightDecoratorClass?: string;
    size?: typeof SIZES.small | typeof SIZES.medium | typeof SIZES.large;
    id?: string;
    inputRefCallback?: (input: HTMLInputElement) => void;
};

export const NumberInput: FC<PropsType> = props => {
    const {
        autoFocus = false,
        disabled = false,
        errorMessageAlignment = DIRECTIONS.left,
        hasError = false,
        horizontalSpacing = SIZES.def,
        locale = 'en-US',
        alignRight,
        alignCenter,
        dataTn,
        errorDataTn,
        errorMessage,
        hasAnimatedPlaceholder,
        label,
        leftDecorator,
        onKeyDown,
        name,
        placeholder,
        rightDecorator,
        rightDecoratorClass,
        size,
        onBlur,
        onChange,
        value,
        id,
        inputRefCallback,
    } = props;

    const inputClassNames = classnames({
        [styles.alignRight]: alignRight,
        [styles.alignCenter]: alignCenter,
    });
    const inputErrorDataTn = errorDataTn ? errorDataTn : `${dataTn}-error`;

    // Takes a string, strips any intl formatting and returns a rounded number
    // returns NaN if empty string is passed or if replace returns an empty string
    const roundStringToNumber = (val: string): number | null => {
        let decimal = '.';
        try {
            // Safari 12 doesn't support formatToParts
            const parts = new Intl.NumberFormat(locale).formatToParts(12345.6);
            // if decimal part is undefined, default to "."
            decimal = parts.find(d => d && d.type === 'decimal')?.value || '.';
        } catch (e) {
            // fail silently
        }
        const replaceNonDigitorDecimalRegex = new RegExp(`[^\\d|${decimal}]`, 'g');
        const replaceDecimalRegex = new RegExp(`[${decimal}]`);

        // replace any non-digit or non-decimal separator with an empty string
        let numStringValue = val.replace(replaceNonDigitorDecimalRegex, '');
        if (decimal !== '.') {
            // if the decimal separator isn't a ".", make it one
            numStringValue = numStringValue.replace(replaceDecimalRegex, '.');
        }
        // coerce string to rounded integer
        const num = Math.round(parseFloat(numStringValue));
        if (isNaN(num)) {
            return null;
        }
        return num;
    };

    const formatValue = (val: string | number | null | undefined): string | undefined => {
        if (typeof val === 'undefined' || val === '') {
            return val;
        }

        if (typeof val === 'string') {
            // convert string val to rounded number
            val = roundStringToNumber(val);
        }
        if (val === null) {
            // there were no digits in the string or bad number was passed
            // or consumers passed null as value
            return '';
        }
        const numberFormatter = new Intl.NumberFormat(locale, {
            maximumFractionDigits: 0,
        });
        return numberFormatter.format(val);
    };

    const handleBlur = (e: FocusEvent<HTMLInputElement>): void => {
        if (onBlur) {
            onBlur(roundStringToNumber(e.target.value));
        }
    };

    // on focus, select the entire given input.
    const handleFocus = (e: FocusEvent<HTMLInputElement>): void => {
        if (document && document.activeElement === e.target) {
            e.target.selectionStart = 0;
            e.target.selectionEnd = e.target.value.length;
        }
    };

    const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
        if (onChange) {
            onChange(roundStringToNumber(e.target.value));
        }
    };

    return (
        <Input
            id={id}
            autoFocus={autoFocus}
            className={inputClassNames}
            dataTn={dataTn}
            disabled={disabled}
            errorDataTn={inputErrorDataTn}
            errorMessage={errorMessage}
            errorMessageAlignment={errorMessageAlignment}
            hasAnimatedPlaceholder={hasAnimatedPlaceholder}
            hasError={hasError}
            horizontalSpacing={horizontalSpacing}
            label={label}
            leftDecorator={leftDecorator}
            maxLength={17}
            name={name}
            onBlur={handleBlur}
            onChange={handleChange}
            onFocus={handleFocus}
            onKeyDown={onKeyDown}
            placeholder={placeholder}
            rightDecorator={rightDecorator}
            rightDecoratorClass={rightDecoratorClass}
            showIcons={false}
            size={size}
            value={formatValue(value)}
            inputRefCallback={inputRefCallback}
        />
    );
};
